import { DataSearchLocation } from '@myosh/odin-components';
import type { OdinDataFilter } from '@myosh/odin-components';
import { isWebView } from '../redux/util/webview-base-query';

/**
 * Returns -1 (fetch all) in WebView mode, otherwise the given default limit.
 * WebView mode pre-fetches all field option data so fields work offline with
 * client-side filtering.
 */
export const getFieldOptionsLimit = (defaultLimit: number): number => {
  return isWebView() ? -1 : defaultLimit;
};

/**
 * Returns `DataSearchLocation.Local` in WebView mode, otherwise the given default.
 * When all data is pre-fetched, filtering must happen client-side.
 */
export const getFieldSearchLocation = (
  defaultLocation: DataSearchLocation = DataSearchLocation.Api
): DataSearchLocation => {
  return isWebView() ? DataSearchLocation.Local : defaultLocation;
};

/**
 * Safely parses a `dependsOn` / parent-field value into a finite number for local
 * filtering.  Returns `undefined` when the value cannot be used as a filter (null,
 * empty string, non-numeric string, NaN, Infinity).
 *
 * Shared by combobox and twin-column hooks to avoid duplicated parsing logic.
 */
export const parseDependsOnValue = (value: string | number | undefined | null): number | undefined => {
  if (value === undefined || value === null) return undefined;

  if (typeof value === 'number') {
    return Number.isFinite(value) && Number.isSafeInteger(value) ? value : undefined;
  }

  const normalized = value.trim();
  if (normalized === '') return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && Number.isSafeInteger(parsed) ? parsed : undefined;
};

/**
 * Filters field options by `parentFieldOptionId` for dependent (cascading) comboboxes.
 * In WebView mode the full unfiltered option set is fetched once; this function
 * replaces the server-side `dependsOn` filter with a local equivalent.
 *
 * @returns The filtered array, or the original array when `parentFieldOptionId` is undefined.
 */
export const filterByParentFieldOptionId = <T extends { parentFieldOptionId?: number | null }>(
  items: T[],
  parentFieldOptionId: number | undefined
): T[] => {
  if (parentFieldOptionId === undefined) return items;
  return items.filter((item) => item.parentFieldOptionId === parentFieldOptionId);
};

/**
 * Filters hierarchy values by `parentId` for `dependsOnParent` hierarchy fields.
 * In WebView mode all hierarchy values are fetched at once; this function
 * replaces the server-side `parentId` filter with a local equivalent.
 *
 * @returns The filtered array, or the original array when `parentId` is undefined.
 */
export const filterByParentId = <T extends { parentId?: number | null }>(
  items: T[],
  parentId: number | undefined
): T[] => {
  if (parentId === undefined) return items;
  return items.filter((item) => item.parentId === parentId);
};

/**
 * Normalizes field filters for WebView mode to maximize prefetch cache hits.
 *
 * The mobile prefetcher caches field options **without** any filter params.
 * ContinuousScroller's archived toggle emits `archived:eq:false` (default OFF
 * state), which creates an `&filter=archived:eq:false` URL param — a cache key
 * mismatch even though the API returns the same data as no filter.
 *
 * This function strips the `archived` filter when its value is `false` (since
 * `archived:eq:false` is semantically equivalent to the API default of returning
 * only non-archived items). This normalizes the cache key to match the
 * prefetcher's URL.
 *
 * `archived:eq:true` is preserved so archived items can still be fetched via
 * online fallback when the user explicitly toggles "show archived".
 *
 * Non-archived filters are passed through unchanged.
 *
 * @returns The normalized filters, or `undefined` when all filters were stripped
 *          (so `createFieldFilterText({ filters: undefined })` emits no URL params).
 */
export const normalizeFieldFilters = (
  fieldFilters: Record<string, OdinDataFilter> | undefined
): Record<string, OdinDataFilter> | undefined => {
  if (!fieldFilters) return undefined;

  const archived = fieldFilters.archived;
  if (archived && archived.value === false && archived.comparison === '=') {
    const { archived: _, ...rest } = fieldFilters;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }

  return Object.keys(fieldFilters).length > 0 ? fieldFilters : undefined;
};
